/**
 * SupplierApprovalModal Component
 * 
 * Modal for approving multiple suppliers with document upload requirement.
 * Shows all suppliers as rows, each with its own file upload.
 * Displays existing documents for re-approval scenarios.
 * Documents are stored in MongoDB GridFS.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import './SupplierApprovalModal.scss';

interface SupplierApprovalModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (approvedIds: string[]) => void;
    suppliers: Array<{ id: string; name: string }>;
    companyId: string;
    companyName?: string;
}

interface ExistingDoc {
    document_id: string;
    filename: string;
}

interface SupplierRow {
    id: string;
    name: string;
    files: File[];
    existingDocs: ExistingDoc[];
    useExisting: boolean;
    uploading: boolean;
    approved: boolean;
    error?: string;
}

const SupplierApprovalModal: React.FC<SupplierApprovalModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    suppliers,
    companyId,
    companyName
}) => {
    const { token } = useAuth();
    const [rows, setRows] = useState<SupplierRow[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

    // Fetch existing documents for each supplier
    const loadExistingDocs = useCallback(async (supplierIds: string[]) => {
        if (!token || !companyId || supplierIds.length === 0) return {};

        const docsMap: { [supplierId: string]: ExistingDoc[] } = {};

        for (const supplierId of supplierIds) {
            try {
                const res = await fetch(
                    `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers/${supplierId}/documents`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                const data = await res.json();
                if (data.success && data.data) {
                    docsMap[supplierId] = data.data.map((d: any) => ({
                        document_id: d.document_id,
                        filename: d.filename
                    }));
                }
            } catch (err) {
                console.error('Error loading docs for', supplierId, err);
            }
        }

        return docsMap;
    }, [token, companyId]);

    // Initialize rows when suppliers change
    useEffect(() => {
        if (!isOpen || !suppliers || suppliers.length === 0) return;

        setLoading(true);

        const initRows = async () => {
            const existingDocsMap = await loadExistingDocs(suppliers.map(s => s.id));

            setRows(suppliers.map(s => ({
                id: s.id,
                name: s.name,
                files: [],
                existingDocs: existingDocsMap[s.id] || [],
                useExisting: (existingDocsMap[s.id]?.length || 0) > 0,
                uploading: false,
                approved: false
            })));

            setLoading(false);
        };

        initRows();
    }, [isOpen, suppliers, loadExistingDocs]);

    // 10MB limit
    const MAX_FILE_SIZE = 10 * 1024 * 1024;

    // Handle file selection for a specific supplier
    const handleFileSelect = useCallback((supplierId: string, selectedFiles: FileList | null) => {
        if (!selectedFiles) return;

        const newFiles = Array.from(selectedFiles);
        const oversizedFiles = newFiles.filter(file => file.size > MAX_FILE_SIZE);

        if (oversizedFiles.length > 0) {
            setRows(prev => prev.map(row =>
                row.id === supplierId
                    ? { ...row, error: `File too large: ${oversizedFiles[0].name}. Max 10MB.` }
                    : row
            ));
            return;
        }

        setRows(prev => prev.map(row =>
            row.id === supplierId
                ? { ...row, files: [...row.files, ...newFiles], error: undefined, useExisting: false }
                : row
        ));
    }, []);

    // Remove file from a supplier row
    const removeFile = useCallback((supplierId: string, fileIndex: number) => {
        setRows(prev => prev.map(row =>
            row.id === supplierId
                ? { ...row, files: row.files.filter((_, i) => i !== fileIndex) }
                : row
        ));
    }, []);

    // Toggle use existing documents
    const toggleUseExisting = useCallback((supplierId: string) => {
        setRows(prev => prev.map(row =>
            row.id === supplierId
                ? { ...row, useExisting: !row.useExisting }
                : row
        ));
    }, []);

    // Approve all suppliers
    const handleApproveAll = async () => {
        // Validate that all suppliers have files or are using existing
        const invalid = rows.filter(r => r.files.length === 0 && !r.useExisting);
        if (invalid.length > 0) {
            setRows(prev => prev.map(row => ({
                ...row,
                error: row.files.length === 0 && !row.useExisting ? 'Add files or use existing documents' : undefined
            })));
            return;
        }

        setIsSubmitting(true);
        const approvedIds: string[] = [];
        // Track uploaded documents for cleanup in case of error
        const uploadedDocsCleanup: Array<{ documentId: string }> = [];

        try {
            for (const row of rows) {
                // Update row status
                setRows(prev => prev.map(r =>
                    r.id === row.id ? { ...r, uploading: true, error: undefined } : r
                ));

                // Upload new files if not using existing
                if (!row.useExisting && row.files.length > 0) {
                    for (const file of row.files) {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('document_type', 'approval_document');

                        const uploadResponse = await fetch(
                            `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers/${row.id}/documents`,
                            {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` },
                                body: formData
                            }
                        );

                        const uploadResult = await uploadResponse.json();
                        if (!uploadResult.success) {
                            throw new Error(`${row.name}: ${uploadResult.error || 'File upload failed'}`);
                        }

                        // Store document ID for potential cleanup
                        // Assuming response format: { success: true, data: { document_id: "...", ... } }
                        if (uploadResult.data && uploadResult.data.document_id) {
                            uploadedDocsCleanup.push({ documentId: uploadResult.data.document_id });
                        }
                    }
                }

                // Approve the supplier
                const approveResponse = await fetch(
                    `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/suppliers/${row.id}/approve`,
                    {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                const approveResult = await approveResponse.json();
                if (approveResult.success) {
                    approvedIds.push(row.id);
                    setRows(prev => prev.map(r =>
                        r.id === row.id ? { ...r, uploading: false, approved: true } : r
                    ));
                } else {
                    throw new Error(`${row.name}: ${approveResult.error || 'Approval failed'}`);
                }
            }

            // All done
            onSuccess?.(approvedIds);
            onClose();
        } catch (err: any) {
            console.error('Approval error:', err);

            // Cleanup: Delete uploaded documents if the process failed
            if (uploadedDocsCleanup.length > 0) {
                console.log('Cleaning up uploaded documents due to error...', uploadedDocsCleanup);
                // We don't await this to avoid blocking the UI update, but ideally we should ensure it runs
                Promise.allSettled(uploadedDocsCleanup.map(doc =>
                    fetch(`${API_ENDPOINTS.BASE_URL}/v2/documents/${doc.documentId}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    }).catch(e => console.error('Cleanup failed for doc:', doc.documentId, e))
                ));
            }

            setRows(prev => prev.map(r => ({
                ...r,
                uploading: false,
                error: r.approved ? undefined : err.message
            })));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setRows([]);
            onClose();
        }
    };

    if (!isOpen) return null;

    const allValid = rows.every(r => r.files.length > 0 || r.useExisting);
    const approvedCount = rows.filter(r => r.approved).length;

    return (
        <div className="supplier-approval-modal-overlay" onClick={handleClose}>
            <div className="supplier-approval-modal multi-row" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Approve Selected Supplier(s) ({rows.length} supplier{rows.length > 1 ? 's' : ''})</h2>
                    <button className="close-btn" onClick={handleClose} disabled={isSubmitting}>×</button>
                </div>

                <div className="modal-body">
                    {companyName && (
                        <div className="company-info">
                            <strong>Company:</strong> {companyName}
                        </div>
                    )}

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
                    ) : (
                        <div className="suppliers-list">
                            {rows.map((row) => (
                                <div key={row.id} className={`supplier-row ${row.approved ? 'approved' : ''} ${row.error ? 'error' : ''}`}>
                                    <div className="row-header">
                                        <span className="supplier-name">{row.name}</span>
                                        {row.uploading && <span className="status uploading">Uploading...</span>}
                                        {row.approved && <span className="status success">✓ Approved</span>}
                                        {row.error && <span className="status error">{row.error}</span>}
                                    </div>

                                    {/* Existing documents section */}
                                    {row.existingDocs.length > 0 && !row.approved && (
                                        <div className="existing-docs">
                                            <label className="use-existing-label">
                                                <input
                                                    type="checkbox"
                                                    checked={row.useExisting}
                                                    onChange={() => toggleUseExisting(row.id)}
                                                    disabled={isSubmitting}
                                                />
                                                <span>Use existing documents ({row.existingDocs.length})</span>
                                            </label>
                                            <div className="existing-files">
                                                {row.existingDocs.map((doc) => (
                                                    <a
                                                        key={doc.document_id}
                                                        href={`${API_ENDPOINTS.BASE_URL}/v2/documents/${doc.document_id}/download`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="existing-file-link"
                                                    >
                                                        {doc.filename}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {!row.approved && (
                                        <div className="row-files">
                                            <div className="files-control">
                                                <button
                                                    className="add-file-btn"
                                                    onClick={() => fileInputRefs.current[row.id]?.click()}
                                                    disabled={isSubmitting}
                                                >
                                                    + Add File
                                                </button>
                                                <input
                                                    ref={(el) => { fileInputRefs.current[row.id] = el; }}
                                                    type="file"
                                                    multiple
                                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                    onChange={(e) => handleFileSelect(row.id, e.target.files)}
                                                    style={{ display: 'none' }}
                                                />
                                            </div>

                                            {row.files.length > 0 && (
                                                <div className="file-chips">
                                                    {row.files.map((file, idx) => (
                                                        <div key={idx} className="file-chip">
                                                            <span>{file.name}</span>
                                                            <button onClick={() => removeFile(row.id, idx)} disabled={isSubmitting}>×</button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <span className="progress-info">
                        {approvedCount > 0 && `${approvedCount}/${rows.length} approved`}
                    </span>
                    <div className="footer-buttons">
                        <button
                            className="btn-cancel"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn-approve"
                            onClick={handleApproveAll}
                            disabled={isSubmitting || !allValid || approvedCount === rows.length || loading}
                        >
                            {isSubmitting ? 'Processing...' : `Approve All (${rows.length})`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupplierApprovalModal;
